import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Stack,
  Divider,
  Skeleton,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import {
  GradingRounded,
  SaveRounded,
  TuneRounded,
} from '@mui/icons-material';
import useGetOrganization from '../../Hooks/useGetOrganization';


// ─── Feature definitions ──────────────────────────────────────────────────────
// Add new features here as the Go struct grows — no other changes needed.
const FEATURE_DEFS = [
  {
    key: 'grader:llm',
    label: 'Assessment grader',
    description: 'Allows tutors to use automatic grader agent, limited to availabilty of tokens',
    icon: GradingRounded,
  },
  {
    key: 'assessment:llm',
    label: 'Assessment create',
    description: 'Create assessments using AI LLMs',
    icon: GradingRounded,
  },
  {
    key: 'students-data:ml',
    label: 'Students data',
    description: 'Enable student data usage with machine learning models',
    icon: GradingRounded,
  },
];


// ─── Single feature row ───────────────────────────────────────────────────────
function FeatureRow({ def, enabled, onChange, disabled }) {
  const Icon = def.icon;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: 2,
        py: 1.75,
        borderRadius: 2,
        border: '0.5px solid',
        borderColor: enabled ? 'primary.light' : 'divider',
        bgcolor: enabled ? 'primary.50' : 'background.paper',
        transition: 'all 0.18s ease',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {/* Left — icon + text */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flex={1} minWidth={0}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: enabled ? 'primary.main' : 'action.hover',
            transition: 'background 0.18s ease',
          }}
        >
          <Icon sx={{ fontSize: 18, color: enabled ? 'white' : 'text.secondary' }} />
        </Box>

        <Box minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              {def.label}
            </Typography>
            <Chip
              label={enabled ? 'Enabled' : 'Disabled'}
              size="small"
              color={enabled ? 'primary' : 'default'}
              variant={enabled ? 'filled' : 'outlined'}
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {def.description}
          </Typography>
        </Box>
      </Stack>

      {/* Right — toggle */}
      <Switch
        checked={enabled}
        onChange={(e) => onChange(def.key, e.target.checked)}
        disabled={disabled}
        color="primary"
        size="small"
        inputProps={{ 'aria-label': def.label }}
      />
    </Stack>
  );
}


// ─── Main component ───────────────────────────────────────────────────────────
const OrganizationFeaturesPanel = () => {
  const { allFeatures, features, loading, fetchFeatures, updateFeatures, fetchAllFeatures } = useGetOrganization()
  // Local draft state — only committed on Save
  const [draft, setDraft]     = useState({});
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState('');

  // Hydrate draft from fetched features
  useEffect(() => {
    fetchFeatures();
    fetchAllFeatures();
  }, []);

  useEffect(() => {
    fetchFeatures();     // org's active subscriptions
    fetchAllFeatures();  // all available features
  }, []);
  
  // Rebuild draft whenever either list changes
  useEffect(() => {
    if (allFeatures?.length) {
      setDraft(buildDraft(allFeatures, features ?? []));
      setDirty(false);
    }
  }, [allFeatures, features]);

  // Build a flat { key: bool } map from the features object
  // Build draft from allFeatures + features (org subscriptions)
function buildDraft(allFeatures, activeFeatures) {
  const activeKeys = new Set(activeFeatures.map(f => f.key));
  return allFeatures.reduce((acc, feature) => {
    acc[feature.key] = activeKeys.has(feature.key);
    return acc;
  }, {});
}

  const handleToggle = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
  
    const activeKeys = new Set((features ?? []).map(f => f.key));
  
    const toAdd = (allFeatures ?? [])
      .filter(f => draft[f.key] && !activeKeys.has(f.key))
      .map(f => ({ id: f.id, key: f.key }));

    const toRemove = (features ?? [])
      .filter(f => !draft[f.key])
      .map(f => ({ id: f.id, key: f.key }));

    
    try {
      // Fire both in parallel — adjust to match your actual API shape
      await Promise.all([
        ...toAdd.map(f    => updateFeatures({ action: 'add',    id: f.id, key: f.key })),
        ...toRemove.map(f => updateFeatures({ action: 'remove', id: f.id, key: f.key })),
      ]);
      setDirty(false);
      await fetchFeatures(); // re-sync with server
    } catch (e) {
      setSaveError('Failed to save. Please try again.');
    }
  
    setSaving(false);
  };

  const handleDiscard = () => {
    if (features?.id) setDraft(buildDraft(features));
    setDirty(false);
    setSaveError('');
  };

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 2.5, border: '0.5px solid', borderColor: 'divider' }}>
        <Skeleton width={140} height={20} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={68} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2.5, py: 1.75, borderBottom: '0.5px solid', borderColor: 'divider' }}
      >
        <TuneRounded fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          Features
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          — {FEATURE_DEFS.length} available
        </Typography>
      </Stack>

      {/* Feature rows */}
      <Stack spacing={1.25} sx={{ p: 2 }}>
        {FEATURE_DEFS.map(def => (
          <FeatureRow
            key={def.key}
            def={def}
            enabled={draft[def.key] ?? false}
            onChange={handleToggle}
            disabled={saving}
          />
        ))}
      </Stack>

      {/* Save error */}
      <Collapse in={!!saveError}>
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="error" variant="outlined" onClose={() => setSaveError('')} sx={{ py: 0.5 }}>
            {saveError}
          </Alert>
        </Box>
      </Collapse>

      {/* Footer — only visible when there are unsaved changes */}
      <Collapse in={dirty}>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            You have unsaved changes
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="text"
              onClick={handleDiscard}
              disabled={saving}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};

export default OrganizationFeaturesPanel;